import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  Grid2 as Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, memo, useRef, useState } from "react";
import { EvaluationMetric } from "types/evaluationMetric";
import { Add, Delete, Edit } from "@mui/icons-material";
import { ModalWithTextArea } from "components/Modal/ModalWithTextArea";
import { CardOutline } from "components/Card";
import { SelectCustomLabel } from "components/CustomLabel/SelectCustomLabel";
import { CustomLabel, CustomLabelType } from "types/CustomLabel";
import { REQUIREMENT } from "helpers/constants";

export const COLUMNS = [
  { label: "Skill Level Score", id: "skill_level" },
  { label: "Milestone Description", id: "description" },
  { label: "Target Achievement Time", id: "target_achievement_time" },
];

export const OPTIONS = [
  { label: "End of First Year", value: 1 },
  { label: "End of Second Year", value: 2 },
  { label: "End of Third Year", value: 3 },
  { label: "End of Fourth Year", value: 4 },
  { label: "End of Fifth Year", value: 5 },
  { label: "Post-Graduation", value: 6 },
];

type TableEvaluationComponentProps = {
  requirementType: string;
  evaluationMetrics: EvaluationMetric[];
  setEvaluationMetrics: (em: EvaluationMetric[]) => void;
  customLabels: CustomLabel[];
};

const TableEvaluationComponent: FC<TableEvaluationComponentProps> = ({
  requirementType,
  evaluationMetrics,
  setEvaluationMetrics,
  customLabels,
}) => {
  const [openMilestoneEdit, setOpenMilestoneEdit] = useState<boolean>(false);
  const indexSelected = useRef<number>(0);
  const [selectedEvaluationMetric, setSelectedEvaluationMetric] =
    useState<EvaluationMetric | null>(null);
  const onChangeValue = (
    index: number,
    key: string,
    value: string | number,
  ) => {
    const newEvaluationMetrics = evaluationMetrics.map((em, i) => {
      if (i === index) {
        return { ...em, [key]: value };
      }
      return em;
    });
    setEvaluationMetrics(newEvaluationMetrics);
  };

  const addNewEvaluationMetric = () => {
    setEvaluationMetrics([
      ...evaluationMetrics,
      {
        skill_level: 1,
        description: "",
        target_achievement_time: 1,
      },
    ]);
  };

  return (
    <Box>
      {evaluationMetrics.length === 0 && (
        <Box pt={1}>
          <Alert severity="info">No Evaluation metric, add more.</Alert>
        </Box>
      )}
      {evaluationMetrics.length > 0 && (
        <>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {evaluationMetrics.map((evaluationMetric, index) => (
              <CardOutline
                key={`evaluationMetric_${index}`}
                sx={{ marginBottom: 2 }}
              >
                <Box sx={{ padding: 1 }}>
                  <Stack direction="column" spacing={2}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectCustomLabel
                          customLabels={customLabels}
                          typeKey={
                            requirementType === REQUIREMENT.REQUIREMENT_EPA
                              ? CustomLabelType.SKILL_LEVEL_SCORE_EPA
                              : CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY
                          }
                          value={evaluationMetric.skill_level}
                          onChange={(val: number) =>
                            onChangeValue(index, "skill_level", val)
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                          <SelectCustomLabel
                            customLabels={customLabels}
                            typeKey={
                              requirementType === REQUIREMENT.REQUIREMENT_EPA
                                ? CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA
                                : CustomLabelType.TARGET_ACHIEVEMENT_TIME_COMPETENCY
                            }
                            value={evaluationMetric.target_achievement_time}
                            onChange={(val: number) =>
                              onChangeValue(
                                index,
                                "target_achievement_time",
                                val,
                              )
                            }
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Box>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        {evaluationMetric.description !== "" ? (
                          <Typography
                            variant="body2"
                            sx={{ paddingRight: "50px", px: 1 }}
                          >
                            {" "}
                            <strong>Description:</strong>{" "}
                            {evaluationMetric.description.length > 64
                              ? evaluationMetric.description.slice(0, 64) +
                                "..."
                              : evaluationMetric.description}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            align="center"
                            color="text.secondary"
                          >
                            No description
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={() => {
                            setEvaluationMetrics(
                              evaluationMetrics.filter((_, i) => i !== index),
                            );
                          }}
                          size="small"
                          startIcon={<Delete />}
                        >
                          Remove
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          onClick={() => {
                            setOpenMilestoneEdit(true);
                            indexSelected.current = index;
                            setSelectedEvaluationMetric(evaluationMetric);
                          }}
                          startIcon={<Edit />}
                        >
                          Edit Description
                        </Button>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              </CardOutline>
            ))}
          </Box>
          <TableContainer sx={{ display: { xs: "none", md: "table" } }}>
            <Table sx={{ minWidth: 650 }} aria-label="table evaluation">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {evaluationMetrics.map((evaluationMetric, index) => (
                  <TableRow
                    key={`evaluationMetric_${index}`}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <SelectCustomLabel
                        customLabels={customLabels}
                        typeKey={
                          requirementType === REQUIREMENT.REQUIREMENT_EPA
                            ? CustomLabelType.SKILL_LEVEL_SCORE_EPA
                            : CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY
                        }
                        value={evaluationMetric.skill_level}
                        onChange={(val: number) =>
                          onChangeValue(index, "skill_level", val)
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0 }}>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        {evaluationMetric.description !== "" && (
                          <Typography
                            variant="body2"
                            sx={{ paddingRight: "50px" }}
                          >
                            {evaluationMetric.description.length > 64
                              ? evaluationMetric.description.slice(0, 64) +
                                "..."
                              : evaluationMetric.description}
                          </Typography>
                        )}
                        <IconButton
                          sx={{
                            position: "absolute",
                            right: 0,
                            top:
                              evaluationMetric.description === ""
                                ? "-15px"
                                : "-5px",
                          }}
                          color="primary"
                          size="small"
                          onClick={() => {
                            setOpenMilestoneEdit(true);
                            indexSelected.current = index;
                            setSelectedEvaluationMetric(evaluationMetric);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <SelectCustomLabel
                          customLabels={customLabels}
                          typeKey={
                            requirementType === REQUIREMENT.REQUIREMENT_EPA
                              ? CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA
                              : CustomLabelType.TARGET_ACHIEVEMENT_TIME_COMPETENCY
                          }
                          value={evaluationMetric.target_achievement_time}
                          onChange={(val: number) =>
                            onChangeValue(index, "target_achievement_time", val)
                          }
                        />
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setEvaluationMetrics(
                            evaluationMetrics.filter((_, i) => i !== index),
                          );
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Box mt={2}>
        <Button
          onClick={addNewEvaluationMetric}
          variant="outlined"
          fullWidth
          startIcon={<Add />}
        >
          {"Add new evaluation metric"}
        </Button>
      </Box>
      {openMilestoneEdit && (
        <ModalWithTextArea
          title="Milestone Description"
          open={openMilestoneEdit}
          defaultValue={selectedEvaluationMetric?.description ?? ""}
          onClose={() => setOpenMilestoneEdit(false)}
          onComplete={(text) => {
            onChangeValue(indexSelected.current, "description", text);
            setOpenMilestoneEdit(false);
          }}
        />
      )}
    </Box>
  );
};

export const TableEvaluation = memo(TableEvaluationComponent);
